import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { PENDING, IDLE } from "const"
import { MarkovState } from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    MarkovGroupProps,
    MarkovStateFilters,
    MarkovGroupStateList,

    LoadAllMarkovStates,
    LoadMarkovGroupStates,
    DeleteMarkovGroupState,
    SortMarkovStates,

    setMarkovStateList,
    setMarkovGroupStateList,
    setAssignedMarkovGroupStateIds,
    setAssignedMarkovGroupStateSort,
    setAssignedMarkovGroupStateFilters,
    setNotAssignedMarkovGroupStateSelected,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    TableSearchWrapper,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import MarkovStateSearch from "client/pages/search/markovStateSearch"
import NotAssignedGroupStates from "../notAssigned"
import Columns from "./columns"


const AssignedGroupStates = ({
    groupId = 0,
}: MarkovGroupProps) => {
    const model = useSelector((state: StoreType) => state.markovModel.modelView)
    const groupList = useSelector((state: StoreType) => state.markovGroup.groupList)
    const stateList = useSelector((state: StoreType) => state.markovState.stateList)
    const groupStateList = useSelector((state: StoreType) => state.markovGroupState.groupStateList)
    const groupStateIds = groupStateList && groupStateList[groupId] ? groupStateList[groupId] : []

    const stateIds = useSelector((state: StoreType) => state.markovGroupState.assignedStates.stateIds)
    const sortColumn = useSelector((state: StoreType) => state.markovGroupState.assignedStates.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.markovGroupState.assignedStates.sortOrder)
    const filters = useSelector((state: StoreType) => state.markovGroupState.assignedStates.filters)
    const reloadList = useSelector((state: StoreType) => state.markovGroupState.reloadGroupStateList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        const promiseArray = [
            LoadAllMarkovStates(model.id).then((res) => {
                dispatch(setMarkovStateList(res))
            }),
            LoadMarkovGroupStates(model.id, groupId).then((groupStates) => {
                const newGroupStateList: MarkovGroupStateList = { [groupId]: groupStates }
                dispatch(setMarkovGroupStateList(newGroupStateList))
            }),
        ]
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedStateIds = SortMarkovStates(Object.values(stateList), sortColumn, sortOrder, filters)
        const sortedAssigned = sortedStateIds.filter((stateId) => groupStateIds.includes(stateId))
        dispatch(setAssignedMarkovGroupStateIds(sortedAssigned))
    }, [groupStateList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MarkovState, order) => {
        dispatch(setAssignedMarkovGroupStateSort(column, order))
    }

    const onFilter = (newFilters: MarkovStateFilters) => {
        dispatch(setAssignedMarkovGroupStateFilters(newFilters))
    }

    const onDelete: DeleteRow = (state: MarkovState, callback) => {
        DeleteMarkovGroupState(model.id, groupId, state.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const newGroupStateList: MarkovGroupStateList = { [groupId]: groupStateIds.filter((id) => id !== state.id) }
                dispatch(setMarkovGroupStateList(newGroupStateList))
            })
        })
    }

    const columns = Columns()
    const group = groupList[groupId] || null

    return (
        <Container>
            <PageHeader>
                <PageTitle>{`"${group && group.name}" ${lang.states}`}</PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                <TableSearchWrapper>
                    <MarkovStateSearch
                        id="assignedGroupStates"
                        rightOffset
                        filters={filters}
                        onFilter={onFilter}
                    />
                    <Button
                        variant="success"
                        onClick={() => {
                            dispatch(setNotAssignedMarkovGroupStateSelected(null))
                            dispatch(addModal({
                                key: "notAssignedGroupStates",
                                content: <NotAssignedGroupStates groupId={groupId} />,
                            }))
                        }}
                    >
                        {lang.add}
                    </Button>
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={stateList}
                    dataIds={stateIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
            </PageContent>
        </Container>
    )
}
export default AssignedGroupStates
